import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BenchmarkChip from "../components/BenchmarkChip";
import { Lock, MessageCircle, Loader2, TrendingUp, Award, Target, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { devWarn } from "../lib/dev-log";

/**
 * Pack 3 · Deliverable 1 — Executive Snapshot™.
 *
 * The one-minute post-discovery executive summary. Reuses the same lead
 * capture flow but re-frames the tiles for executive readability:
 *   • Business Growth Index™                   (was: Overall Pulse)
 *   • Overall Performance Indicator            (was: Risk Level)
 *   • Top 3 Business Opportunities             (top gaps)
 *   • Strongest Business Capability            (highest category score)
 *   • Industry Benchmark Summary               (reuses BenchmarkChip)
 *   • Recommended Next Step                    (unlock — capture form)
 */
export default function Results() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", company: "", position: "", email: "", mobile: "" });
  const [busy, setBusy] = useState(false);
  const [progressStep, setProgressStep] = useState(0);

  useEffect(() => {
    const cached = sessionStorage.getItem(`bp_results_${id}`);
    if (cached) setData(JSON.parse(cached));
    else toast.error("Business Growth Discovery™ results not found. Please restart.");
  }, [id]);

  useEffect(() => {
    if (!busy) { setProgressStep(0); return; }
    const t = setInterval(() => setProgressStep((s) => (s + 1) % 4), 1100);
    return () => clearInterval(t);
  }, [busy]);

  // Strongest capability — the highest-scoring category (ties broken by first).
  const strongest = useMemo(() => {
    const scores = data?.category_scores || {};
    const entries = Object.entries(scores);
    if (!entries.length) return null;
    return entries.reduce((best, cur) => (cur[1] > best[1] ? cur : best), entries[0]);
  }, [data]);

  if (!data) return <div className="p-10 text-muted-foreground">Loading results…</div>;

  const sendWhatsApp = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your first name and business email");
      return;
    }
    // POPUP-BLOCKER FIX: open a placeholder tab SYNCHRONOUSLY inside the
    // click handler so we retain the user-gesture context. If we wait until
    // the ~20-40s /leads/capture round-trip resolves and only THEN call
    // window.open(), Safari (and increasingly Chrome/iOS) treat the tab as
    // programmatic and silently block it.
    //
    // IMPORTANT: we MUST NOT pass the `noopener` feature here. Per the HTML
    // spec, `noopener` forces window.open() to return null, which would
    // discard the handle we need to update the tab's location once the API
    // resolves. We restore opener-isolation immediately after the redirect
    // by setting waTab.opener = null.
    const waTab = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const { data: res } = await api.post(
        "/leads/capture",
        { assessment_id: id, ...form },
        { timeout: 120000 }
      );
      sessionStorage.setItem(`bp_lead_${res.lead_id}`, JSON.stringify(res));
      if (res.whatsapp_link) {
        if (waTab && !waTab.closed) {
          try {
            waTab.location.href = res.whatsapp_link;
            // Break the opener relationship for parity with rel="noopener".
            try { waTab.opener = null; } catch (err) {
              // Cross-origin after nav is expected — the DOM guard here is
              // best-effort. Surface in dev so a real bug can't hide behind
              // a silent catch; in production `devWarn` compiles to no-op.
              devWarn("[Results] waTab.opener cleanup skipped (cross-origin)", err);
            }
          } catch {
            waTab.close();
            // Last-resort fallback if we lost access to the tab.
            const opened = window.open(res.whatsapp_link, "_blank", "noopener");
            if (!opened) {
              toast.message("Please open WhatsApp manually — your report link is in the message.", { duration: 6000 });
            }
          }
        } else {
          // Sync pre-open was blocked entirely by the browser.
          const opened = window.open(res.whatsapp_link, "_blank", "noopener");
          if (!opened) {
            toast.message("Please open WhatsApp manually — your report link is in the message.", { duration: 6000 });
          }
        }
      } else {
        if (waTab && !waTab.closed) waTab.close();
        toast.message("Consultant has no WhatsApp configured — taking you to your report.");
      }
      nav(`/report/${res.lead_id}`);
    } catch (e) {
      if (waTab && !waTab.closed) waTab.close();
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not generate report. Please retry.");
    } finally {
      setBusy(false);
    }
  };

  const getScoreColor = (s) => {
    if (s >= 75) return "text-emerald-400";
    if (s >= 55) return "text-accent";
    if (s >= 35) return "text-amber-400";
    return "text-red-400";
  };
  const scoreColor = getScoreColor(data.overall_score);

  const progressMessages = [
    "Capturing your details securely…",
    "Preparing your consultant handoff…",
    "Opening WhatsApp with your Discovery Meeting request…",
    "Redirecting you to your report…",
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="executive-snapshot">
      <div className="overline"><span className="accent-line" /> Executive Snapshot™</div>
      <h1 className="font-display text-4xl mt-3">Your one-minute executive summary</h1>
      <p className="text-muted-foreground mt-2">{data.country} · {data.industry} · {data.function}</p>

      {/* Business Growth Index™ + Performance Indicator + Top Opportunities */}
      <Card className="card-elevated mt-8 p-8 grid md:grid-cols-3 gap-6" data-testid="snapshot-headline">
        <div className="text-center md:border-r md:border-border/50 md:pr-6">
          <div className="overline">Business Growth Index™</div>
          <div className={`font-display text-7xl mt-3 ${scoreColor}`} data-testid="result-score">{data.overall_score}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">out of 100</div>
        </div>
        <div className="text-center md:border-r md:border-border/50 md:px-6">
          <div className="overline">Overall Performance Indicator</div>
          <div className="font-display text-4xl mt-3" data-testid="result-risk">{data.risk_level}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">performance rating</div>
        </div>
        <div className="md:pl-6">
          <div className="overline">Top 3 Business Opportunities</div>
          <ul className="mt-3 space-y-2" data-testid="snapshot-opportunities">
            {data.top_gaps.map((g, i) => (
              <li key={g.area} className="flex items-center justify-between text-sm" data-testid={`result-gap-${i}`}>
                <span>{i + 1}. {g.area}</span>
                <span className="text-muted-foreground">{g.score}/100</span>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      {/* Strongest Capability + Industry Benchmark */}
      <div className="grid md:grid-cols-2 gap-5 mt-5">
        <Card className="card-elevated p-6" data-testid="snapshot-strongest">
          <div className="flex items-center gap-2 overline">
            <Award className="w-3.5 h-3.5 text-accent" /> Strongest Business Capability
          </div>
          {strongest ? (
            <>
              <div className="font-display text-2xl mt-3">{strongest[0]}</div>
              <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400" style={{ width: `${strongest[1]}%` }} />
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Scoring <span className="text-emerald-300 font-medium">{strongest[1]}/100</span> — protect this and leverage it in the next 90 days.
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground mt-3">Not enough data.</div>
          )}
        </Card>

        <Card className="card-elevated p-6" data-testid="snapshot-benchmark">
          <div className="flex items-center gap-2 overline">
            <TrendingUp className="w-3.5 h-3.5 text-accent" /> Industry Benchmark Summary
          </div>
          <div className="mt-4">
            <BenchmarkChip
              country={data.country}
              industry={data.industry}
              fn={data.function}
              sizeBand={data.prospect_company_size_band}
              score={data.overall_score}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-3 leading-relaxed">
            How your score compares against anonymised peers in {data.industry} · {data.country}.
          </div>
        </Card>
      </div>

      {/* Executive summary teaser */}
      <Card className="card-elevated mt-5 p-7">
        <div className="overline">Executive summary (teaser)</div>
        <p className="mt-3 text-lg leading-relaxed">{data.teaser_summary}</p>
      </Card>

      {/* Recommended Next Step — lead capture */}
      <Card className="card-elevated mt-8 p-8 relative overflow-hidden" data-testid="snapshot-next-step">
        <Lock className="absolute top-4 right-4 w-5 h-5 text-accent" />
        <div className="flex items-center gap-2 overline">
          <Target className="w-3.5 h-3.5 text-accent" /> Recommended Next Step
        </div>
        <h2 className="font-display text-3xl mt-3">
          Your personalised Executive Growth Report™ is ready. Tell us where you would like it delivered.
        </h2>
        <p className="text-muted-foreground mt-3 text-sm">
          The full report — Business Growth Index™, opportunity analysis, key risks, a 30/90/180-day Priority Action Roadmap, and your consultant&apos;s perspective — will be delivered once your consultant has reviewed it.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mt-7">
          <Field label="First name" required>
            <Input data-testid="lead-name" value={form.name} placeholder="Your first name" onChange={(e) => setForm({...form, name: e.target.value})} />
          </Field>
          <Field label="Business email" required>
            <Input data-testid="lead-email" type="email" value={form.email} placeholder="you@company.com" onChange={(e) => setForm({...form, email: e.target.value})} />
          </Field>
          <Field label="Company" hint="Optional">
            <Input data-testid="lead-company" value={form.company} placeholder="Company name" onChange={(e) => setForm({...form, company: e.target.value})} />
          </Field>
          <Field label="Position / Title" hint="Optional">
            <Input data-testid="lead-position" value={form.position} placeholder="e.g. CEO, MD, Founder" onChange={(e) => setForm({...form, position: e.target.value})} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Mobile / WhatsApp" hint="Optional — helps your consultant reach you faster">
              <Input data-testid="lead-mobile" value={form.mobile} placeholder="+country code and number" onChange={(e) => setForm({...form, mobile: e.target.value})} />
            </Field>
          </div>
        </div>

        <div className="mt-7">
          <Button data-testid="lead-whatsapp-btn" disabled={busy} onClick={sendWhatsApp}
            className="w-full md:w-auto bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-7 py-6 text-base">
            {busy ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {progressMessages[progressStep]}
              </>
            ) : (
              <>
                <MessageCircle className="w-5 h-5 mr-2" />
                Send WhatsApp message to receive your full report
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          {busy && (
            <p className="text-xs text-muted-foreground mt-3 max-w-md" data-testid="lead-progress-note">
              This takes ~30 seconds. Please don&apos;t close this tab.
            </p>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-6">
          Your details are shared only with your chosen consultant. We never sell your data.
        </p>
      </Card>
    </div>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">
          {label}{required && <span className="text-accent"> *</span>}
        </Label>
        {hint && !required && (
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/70">{hint}</span>
        )}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
